import axios from 'axios'
import { 
  IUser, 
  Project, 
  Task, 
  IAttendance, 
  LoginRequest, 
  RegisterRequest, 
  AuthResponse,
  Report,
  ProjectStats,
  IActivityLog,
  CreateUserData,
  UpdateUserData,
  News,
  CreateNewsRequest,
  UpdateNewsRequest,
  UploadResponse,
  PaginatedNewsResponse,
  NewsListParams,
  CreateStatusRequest,
  UpdateStatusRequest,
  StatusDefinition,
  DeductionTypeDefinition,
  TimesheetData,
  TimeLogEntry,
  CreateTimeLogRequest,
  UpdateTimeLogRequest,
  LDAPUser,
  LDAPSyncResult,
  LDAPConnectionTest,
  ApprovalEmployeeData,
  ApproveTimeLogsRequest,
  RejectTimeLogsRequest
} from './types'
import { clientStorage, isClient } from './client-utils'
import { getErrorMessage, getErrorStatus, logError, isAxiosError } from './error-handler'

interface EnhancedError extends Error {
  response?: unknown
  originalError?: unknown
}

const api = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: true,
  // Увеличенный таймаут для тяжелых запросов (отчеты, табели)
  timeout: 90000 // 90 секунд
})

console.log('API URL:', process.env.NEXT_PUBLIC_API_URL)

// Вспомогательная функция для нормализации объекта проекта
const normalizeProject = (project: Project): Project => {
  // Создаем копию проекта с базовыми полями
  const normalizedProject: Project = {
    id: project.id,
    name: project.name,
    fullName: project.fullName || '',
    description: project.description || '',
    contractNumber: project.contractNumber || '',
    status: project.status || 'active',
    responsibleId: project.responsibleId, 
    responsibleUser: project.responsibleUser, 
    responsibleName: project.responsibleName || '',
    responsiblePhone: project.responsiblePhone || '',
    responsibleEmail: project.responsibleEmail || '',
    clientName: project.clientName || '',
    clientContact: project.clientContact || '',
    workObjects: project.workObjects || '',
    startDate: project.startDate,
    endDate: project.endDate,
    warrantyStartDate: project.warrantyStartDate,
    warrantyEndDate: project.warrantyEndDate,
    groupId: project.groupId, // Добавляем groupId
    createdAt: project.createdAt,
    updatedAt: project.updatedAt
  };
  
  // Добавляем сотрудников, если они есть
  if (project.users && Array.isArray(project.users)) {
    // Нормализуем сотрудников, чтобы избежать циклических ссылок
    normalizedProject.users = project.users.map(user => ({
      id: user.id,
      name: user.name,
      email: user.email,
      accessLevel: user.accessLevel,
      employmentType: user.employmentType || 'permanent',
      timeLoggingEnabled: user.timeLoggingEnabled !== undefined ? user.timeLoggingEnabled : true,
      chatId: user.chatId, // Добавляем chatId
      createdAt: user.createdAt,
      updatedAt: user.updatedAt
    }));
  }
  
  // Добавляем задачи, если они есть
  if (project.tasks && Array.isArray(project.tasks)) {
    normalizedProject.tasks = project.tasks;
  }
  
  return normalizedProject;
};

// Add request interceptor for debugging
api.interceptors.request.use((config) => {
  // Add cache control headers to each request
  config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate'
  config.headers['Pragma'] = 'no-cache'
  config.headers['Expires'] = '0'

  console.log('Request:', {
    url: config.url,
    method: config.method,
    headers: config.headers,
    data: config.data
  })
  return config
}, (error) => {
  console.error('Request error:', error)
  return Promise.reject(error)
})

// Add response interceptor for debugging
api.interceptors.response.use((response) => {
  console.log('Response:', {
    status: response.status,
    headers: response.headers,
    data: response.data
  })
  return response
}, (error) => {
  // Структурированное логирование ошибок
  const errorInfo = {
    message: error.message || 'Unknown error',
    code: error.code,
    name: error.name,
    stack: process.env.NODE_ENV === 'development' ? error.stack : undefined,
    config: {
      url: error.config?.url,
      method: error.config?.method,
      headers: error.config?.headers,
      // Не логируем чувствительные данные
      data: error.config?.data ? '(request data omitted)' : undefined
    },
    response: error.response ? {
      status: error.response.status,
      statusText: error.response.statusText,
      data: error.response.data,
      headers: error.response.headers
    } : undefined,
    isNetworkError: !error.response && error.request,
    isRequestError: !error.response && !error.request
  }

  // В зависимости от типа ошибки выводим разные сообщения
  if (errorInfo.isNetworkError) {
    console.error('Network error - no response received:', errorInfo)
  } else if (errorInfo.isRequestError) {
    console.error('Request setup error:', errorInfo)
  } else if (error.response) {
    console.error(`Server error (${error.response.status}):`, errorInfo)
  } else {
    console.error('Unexpected error:', errorInfo)
  }

  // Добавляем дополнительную информацию к объекту ошибки
  error.errorInfo = errorInfo

  return Promise.reject(error)
})

// Add token to requests
api.interceptors.request.use((config) => {
  if (isClient) {
    const token = clientStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
  }
  return config
})

// Auth API
export const auth = {
  login: async (data: LoginRequest): Promise<AuthResponse> => {
    try {
      console.log('Login attempt with data:', data)
      console.log('API URL:', process.env.NEXT_PUBLIC_API_URL)
      const response = await api.post<AuthResponse>('/auth/login', data)
      console.log('Login successful:', response.data)
      return response.data
    } catch (error: unknown) {
      console.error('Login failed:', error)
      throw new Error(getErrorMessage(error, 'Ошибка при входе в систему'))
    }
  },

  register: async (data: RegisterRequest): Promise<AuthResponse> => {
    try {
      const response = await api.post<AuthResponse>('/auth/register', data)
      return response.data
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error, 'Ошибка при регистрации'))
    }
  },

  getCurrentUser: async (): Promise<AuthResponse> => {
    try {
      const response = await api.get<IUser>('/user')
      return {
        user: response.data,
        token: clientStorage.getItem('token') || ''
      }
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error, 'Ошибка получения данных сотрудника'))
    }
  },

  logout: () => {
    clientStorage.removeItem('token')
  }
}

// Projects API
export const projects = {
  create: async (data: { 
    name: string, 
    fullName?: string,
    description?: string, 
    status?: string,
    contractNumber?: string,
    responsibleId?: number,
    responsibleName?: string,
    responsiblePhone?: string,
    responsibleEmail?: string,
    clientName?: string,
    clientContact?: string,
    workObjects?: string,
    startDate?: string,
    endDate?: string,
    warrantyStartDate?: string,
    warrantyEndDate?: string,
    groupId?: number
  }): Promise<Project> => {
    const response = await api.post<Project>('/projects', { 
      name: data.name,
      fullName: data.fullName || '',
      status: data.status || 'active',
      description: data.description || '',
      contractNumber: data.contractNumber || '',
      responsibleId: data.responsibleId,
      responsibleName: data.responsibleName || '',
      responsiblePhone: data.responsiblePhone || '',
      responsibleEmail: data.responsibleEmail || '',
      clientName: data.clientName || '',
      clientContact: data.clientContact || '',
      workObjects: data.workObjects || '',
      startDate: data.startDate || '',
      endDate: data.endDate || '',
      warrantyStartDate: data.warrantyStartDate || '',
      warrantyEndDate: data.warrantyEndDate || '',
      groupId: data.groupId
    })
    return response.data
  },

  list: async (): Promise<Project[]> => {
    try {
      console.log('Fetching projects from API...');
      const timestamp = new Date().getTime();
      // Добавляем параметр include для получения связанных сотрудников
      const response = await api.get<Project[]>(`/projects?include=users&_t=${timestamp}`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      console.log('Projects API response:', response);
      
      if (!response.data) {
        console.error('No data received from projects API');
        throw new Error('No data received from API');
      }

      if (!Array.isArray(response.data)) {
        console.error('Invalid projects data format:', response.data);
        throw new Error('Invalid data format received from API');
      }

      // Проверяем наличие данных о сотрудниках
      response.data.forEach(project => {
        if (project.users) {
          console.log(`Project ${project.id} (${project.name}) has ${project.users.length} users`);
        } else {
          console.log(`Project ${project.id} (${project.name}) has no users array or it's empty`);
        }
      });

      console.log(`Fetched ${response.data.length} projects:`, response.data);
      
      // Нормализуем все проекты перед возвратом
      return response.data.map(normalizeProject);
    } catch (error: unknown) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  },

  get: async (id: number): Promise<Project> => {
    // Добавляем метку времени и заголовки для предотвращения кэширования
    const timestamp = new Date().getTime();
    console.log(`Fetching project ${id} with users...`);
    
    try {
      // Добавляем параметр include для получения связанных сотрудников
      const response = await api.get<Project>(`/projects/${id}?include=users&_t=${timestamp}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      console.log(`Fetched project ${id} details:`, response.data);
      
      // Проверяем наличие данных о сотрудниках
      if (response.data.users) {
        console.log(`Project ${id} has ${response.data.users.length} users:`, 
          response.data.users.map(u => `${u.id} (${u.name})`));
      } else {
        console.log(`Project ${id} has no users array or it's empty`);
      }
      
      // Нормализуем проект перед возвратом
      return normalizeProject(response.data);
    } catch (error: unknown) {
      console.error(`Error fetching project ${id}:`, error);
      throw error;
    }
  },

  update: async (id: number, data: Partial<Project>): Promise<Project> => {
    const response = await api.put<Project>(`/projects/${id}`, { 
      name: data.name,
      fullName: data.fullName || '',
      status: data.status || 'active',
      description: data.description || '',
      contractNumber: data.contractNumber || '',
      responsibleId: data.responsibleId,
      responsibleName: data.responsibleName || '',
      responsiblePhone: data.responsiblePhone || '',
      responsibleEmail: data.responsibleEmail || '',
      clientName: data.clientName || '',
      clientContact: data.clientContact || '',
      workObjects: data.workObjects || '',
      startDate: data.startDate || '',
      endDate: data.endDate || '',
      warrantyStartDate: data.warrantyStartDate || '',
      warrantyEndDate: data.warrantyEndDate || '',
      groupId: data.groupId
    })
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/projects/${id}`)
  },

  assignUser: async (projectId: number, userId: number): Promise<void> => {
    // Добавляем метку времени для предотвращения кэширования
    const timestamp = new Date().getTime();
    console.log(`Assigning user ${userId} to project ${projectId}`);
    
    try {
      // Подробное логирование запроса
      console.log(`API URL: ${process.env.NEXT_PUBLIC_API_URL}/projects/${projectId}/users/${userId}`);
      
      // Делаем запрос на назначение сотрудника
      // Важно! API эндпоинт должен быть точно таким же, как в backend/internal/router/router.go 
      // api.POST("/projects/:id/users/:userID", middleware.RequireAccessLevel(models.ProjectManagerLevel), projectHandler.AssignUser)
      const response = await api.post(`/projects/${projectId}/users/${userId}?_t=${timestamp}`, {}, {
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      console.log(`User ${userId} assigned to project ${projectId} successfully, response:`, response);
    } catch (error: unknown) {
      logError(`Error assigning user ${userId} to project ${projectId}`, error);
      throw error;
    }
  },
  
  unassignUser: async (projectId: number, userId: number): Promise<void> => {
    // Добавляем метку времени для предотвращения кэширования
    const timestamp = new Date().getTime();
    console.log(`Unassigning user ${userId} from project ${projectId}`);
    
    try {
      // Подробное логирование запроса
      console.log(`API URL: ${process.env.NEXT_PUBLIC_API_URL}/projects/${projectId}/users/${userId}`);
      
      // Делаем запрос на снятие назначения сотрудника
      const response = await api.delete(`/projects/${projectId}/users/${userId}?_t=${timestamp}`, {
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      console.log(`User ${userId} unassigned from project ${projectId} successfully, response:`, response);
    } catch (error: unknown) {
      logError(`Error unassigning user ${userId} from project ${projectId}`, error);
      throw error;
    }
  }
}

// Tasks API
export const tasks = {
  create: async (data: Partial<Task>): Promise<Task> => {
    // Ensure projectId and userId are converted to numbers
    const taskData = {
      title: data.title,
      description: data.description || '',
      projectId: data.projectId ? Number(data.projectId) : undefined,
      userId: data.userId ? Number(data.userId) : undefined,
      status: data.status || 'pending',
      priority: data.priority || 'medium',
      completed: false,
      dueDate: data.dueDate
    }
    
    console.log('Sending task data:', taskData);
    const response = await api.post<Task>('/tasks', taskData);
    console.log('Task response:', response.data);
    return response.data;
  },

  list: async (): Promise<Task[]> => {
    try {
      console.log('Fetching tasks from API...');
      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      const response = await api.get<Task[]>(`/tasks?_t=${timestamp}`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      console.log('Tasks API response:', response);
      
      if (!response.data) {
        console.error('No data received from tasks API');
        throw new Error('No data received from API');
      }

      if (!Array.isArray(response.data)) {
        console.error('Invalid tasks data format:', response.data);
        throw new Error('Invalid data format received from API');
      }

      console.log(`Fetched ${response.data.length} tasks:`, response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('Error fetching tasks:', error);
      throw error; // Передаем ошибку для обработки на уровне UI
    }
  },

  get: async (id: number): Promise<Task> => {
    const response = await api.get<Task>(`/tasks/${id}`)
    return response.data
  },

  update: async (id: number, data: Partial<Task>): Promise<Task> => {
    // Ensure all required fields are included in the update
    // and convert string IDs to numbers
    const taskData = {
      userId: data.userId ? Number(data.userId) : undefined,
      title: data.title || '',
      description: data.description || '',
      projectId: data.projectId ? Number(data.projectId) : undefined,
      status: data.status || 'pending',
      priority: data.priority || 'medium',
      completed: data.completed !== undefined ? data.completed : false,
      dueDate: data.dueDate || new Date().toISOString()
    }
    
    console.log('Updating task data for ID', id, ':', taskData);
    const response = await api.put<Task>(`/tasks/${id}`, taskData);
    console.log('Update task response:', response.data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/tasks/${id}`)
  }
}

// Attendance API
export const attendance = {
  list: async (forceRefresh: boolean = false): Promise<IAttendance[]> => {
    try {
      console.log('Fetching attendance records from API...');
      const url = forceRefresh ? `/attendance?_t=${new Date().getTime()}` : '/attendance';
      const response = await api.get<IAttendance[]>(url, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (!response.data) {
        console.error('No data received from attendance API');
        throw new Error('No data received from API');
      }

      if (!Array.isArray(response.data)) {
        console.error('Invalid attendance data format:', response.data);
        throw new Error('Invalid data format received from API');
      }

      return response.data;
    } catch (error: unknown) {
      console.error('Error fetching attendance records:', error);
      throw error;
    }
  },

  checkIn: async (data: { 
    projectId: number;
    latitude: number;
    longitude: number;
    notes?: string;
  }, targetUserId?: number) => {
    const headers: Record<string, string> = {};
    if (targetUserId) {
      headers['X-Target-User-ID'] = targetUserId.toString();
    }
    
    try {
      const response = await api.post('/attendance/check-in', data, { headers });
      return response.data;
    } catch (error: unknown) {
      console.error("Check-in error:", error);
      const errorMessage = getErrorMessage(error, 'Ошибка при отметке прихода');
      const enhancedError = new Error(errorMessage);
      if (isAxiosError(error)) {
        (enhancedError as EnhancedError).response = error.response;
      }
      (enhancedError as EnhancedError).originalError = error;
      throw enhancedError;
    }
  },

  checkOut: async (attendanceId: number, overtimeReason?: string): Promise<IAttendance> => {
    console.log(`Calling check-out for attendance ID: ${attendanceId}`, {
      type: typeof attendanceId,
      value: attendanceId,
      overtimeReason
    });
    
    // Создаем объект с правильным названием поля для API
    const requestData = { 
      attendanceId: attendanceId,
      overtimeReason: overtimeReason
    };
    console.log("Request data:", requestData);
    
    try {
      const response = await api.post<IAttendance>('/attendance/check-out', requestData);
      console.log("Check-out response:", response);
      return response.data;
    } catch (error: unknown) {
      console.error("Check-out error:", error);
      const errorMessage = getErrorMessage(error, 'Ошибка при отметке ухода');
      const enhancedError = new Error(errorMessage);
      if (isAxiosError(error)) {
        (enhancedError as EnhancedError).response = error.response;
      }
      (enhancedError as EnhancedError).originalError = error;
      throw enhancedError;
    }
  },

  getProjectAttendances: async (projectId: string | number, filter?: 'active' | 'completed' | 'all', timestamp?: number): Promise<IAttendance[]> => {
    let url = `/attendance/project/${projectId}`;
    
    // Start with query parameters
    const params = new URLSearchParams();
    
    // Add filter parameter if specified
    if (filter === 'active') {
      params.append('active', 'true');
    } else if (filter === 'completed') {
      params.append('active', 'false');
    }
    
    // Add timestamp to prevent caching
    params.append('_t', String(timestamp || new Date().getTime()));
    
    // Add query string to URL
    const queryString = params.toString();
    url += `?${queryString}`;
    
    console.log(`Fetching attendances for project ${projectId} with filter: ${filter || 'all'}, URL: ${url}`);
    const response = await api.get<IAttendance[]>(url, {
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    return response.data;
  },

  // Status-related methods
  createStatus: async (data: CreateStatusRequest): Promise<IAttendance> => {
    try {
      console.log('Creating status record with data:', data);
      const response = await api.post<IAttendance>('/attendance/status', data);
      console.log('Status record created:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('Error creating status record:', error);
      throw error;
    }
  },

  updateStatus: async (id: number, data: UpdateStatusRequest): Promise<IAttendance> => {
    try {
      console.log(`Updating status record ${id} with data:`, data);
      const response = await api.put<IAttendance>(`/attendance/status/${id}`, data);
      console.log('Status record updated:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('Error updating status record:', error);
      throw error;
    }
  },

  getStatusDefinitions: async (): Promise<StatusDefinition[]> => {
    try {
      console.log('Fetching status definitions...');
      const response = await api.get<StatusDefinition[]>('/attendance/statuses');
      console.log('Status definitions fetched:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('Error fetching status definitions:', error);
      throw error;
    }
  },

  /** Справочник «Непроектные списания» (deduction_types) */
  getDeductionTypes: async (): Promise<DeductionTypeDefinition[]> => {
    try {
      const response = await api.get<DeductionTypeDefinition[]>('/attendance/deduction-types');
      return response.data;
    } catch (error: unknown) {
      console.error('Error fetching deduction types:', error);
      throw error;
    }
  },

  getUserDayStatus: async (userId: number, date?: string): Promise<{
    userId: number;
    date: string;
    hasActiveCheckIn: boolean;
    hasTimeLogged: boolean;
    canCheckIn: boolean;
    canLogTime: boolean;
    totalHours: number;
    message?: string;
  }> => {
    try {
      console.log(`Fetching day status for user ${userId}, date: ${date || 'today'}`);
      const queryParams = date ? `?date=${date}` : '';
      const response = await api.get(`/attendance/user/${userId}/day-status${queryParams}`);
      console.log('User day status fetched successfully:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('Error fetching user day status:', error);
      
      let errorMessage = 'Ошибка при получении статуса сотрудника';
      const status = getErrorStatus(error);
      
      if (status === 404) {
        errorMessage = 'Сотрудник не найден';
      } else if (status === 403) {
        errorMessage = 'Нет доступа к информации о сотруднике';
      } else {
        errorMessage = getErrorMessage(error, errorMessage);
      }
      
      const enhancedError = new Error(errorMessage);
      if (isAxiosError(error)) {
        (enhancedError as EnhancedError).response = error.response;
      }
      (enhancedError as EnhancedError).originalError = error;
      
      throw enhancedError;
    }
  },

  // Получить незакрытые смены сотрудника за предыдущие дни
  getUnclosedShifts: async (userId: number): Promise<IAttendance[]> => {
    try {
      console.log(`Fetching unclosed shifts for user ${userId}...`);
      const response = await api.get<IAttendance[]>(`/attendance/user/${userId}/unclosed-shifts`);
      return response.data || [];
    } catch (error: unknown) {
      console.error('Error fetching unclosed shifts:', error);
      throw error;
    }
  },

  // Запрос на подтверждение ухода через Telegram
  requestCheckoutApproval: async (data: {
    attendanceId?: number;
    requiresApproval: boolean;
    overtimeReason?: string;
  }): Promise<{
    message: string;
    attendance: IAttendance;
    responsible: string;
  }> => {
    try {
      console.log('Requesting checkout approval:', data);
      const response = await api.post('/attendance/request-checkout-approval', data);
      console.log('Checkout approval request sent:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('Error requesting checkout approval:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при запросе подтверждения ухода'));
    }
  },

  // Получить все attendance записи конкретного пользователя
  getUserAttendances: async (
    userId: number, 
    startDate?: string, 
    endDate?: string, 
    type?: 'work' | 'status'
  ): Promise<IAttendance[]> => {
    try {
      console.log(`Fetching attendances for user ${userId}`, { startDate, endDate, type });
      
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (type) params.append('type', type);
      
      const url = `/attendance/user/${userId}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await api.get<IAttendance[]>(url);
      
      console.log(`User ${userId} attendances fetched successfully:`, response.data.length, 'records');
      return response.data;
    } catch (error: unknown) {
      console.error(`Error fetching attendances for user ${userId}:`, error);
      const errorMessage = getErrorMessage(error, 'Ошибка при получении записей посещаемости');
      const enhancedError = new Error(errorMessage);
      if (isAxiosError(error)) {
        (enhancedError as EnhancedError).response = error.response;
      }
      (enhancedError as EnhancedError).originalError = error;
      throw enhancedError;
    }
  }
}

// Helper function to determine if user should use personal endpoints
const shouldUsePersonalEndpoints = (): boolean => {
  if (!isClient) return false;
  try {
    // Сначала пробуем получить пользователя из localStorage
    const userData = clientStorage?.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      console.log('Checking user access level from localStorage:', user.accessLevel);
      return user.accessLevel === 'engineer' || user.accessLevel === 'senior-engineer' || user.accessLevel === 'department-head' || user.accessLevel === 'implementation-head';
    }
    
    // Если в localStorage нет, пробуем достать из токена
    const token = clientStorage?.getItem('token');
    if (token) {
      try {
        const tokenData = JSON.parse(atob(token.split('.')[1]));
        console.log('Checking user access level from token:', tokenData.accessLevel);
        return tokenData.accessLevel === 'engineer' || tokenData.accessLevel === 'senior-engineer' || tokenData.accessLevel === 'department-head' || tokenData.accessLevel === 'implementation-head';
      } catch (tokenError) {
        console.error('Error parsing token for access level:', tokenError);
      }
    }
    
    console.log('No user data found, defaulting to full endpoints');
    return false;
  } catch (error: unknown) {
    console.error('Error checking user access level:', error);
    return false;
  }
};

// Reports API
export const reports = {
  getAttendance: async (params: { startDate?: string, endDate?: string, _t?: number }): Promise<Report[]> => {
    try {
      // Convert params to URLSearchParams, ensuring all values are strings
      const queryParams = new URLSearchParams();
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params._t) queryParams.append('_t', params._t.toString());
      
      // Determine endpoint based on user role
      const usePersonalEndpoint = shouldUsePersonalEndpoints();
      const endpoint = usePersonalEndpoint ? '/reports/personal/attendance' : '/reports/attendance';
      
      console.log(`Fetching attendance reports with params: ${queryParams.toString()}, using endpoint: ${endpoint}, usePersonalEndpoint: ${usePersonalEndpoint}`);
      
      try {
        const response = await api.get<Report[]>(`${endpoint}?${queryParams}`, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        if (!response.data) {
          console.error('No data received from attendance reports API');
          throw new Error('No data received from API');
        }

        if (!Array.isArray(response.data)) {
          console.error('Invalid attendance reports data format:', response.data);
          throw new Error('Invalid data format received from API');
        }

        // Логируем полученные данные
        console.log('Raw response data:', response.data);

        // Проверяем каждую запись и логируем для отладки
        const reports = response.data.map(record => {
          console.log('Processing report record:', record);
          return {
            ...record,
            // Убеждаемся, что все поля присутствуют
            date: record.date || record.checkInTime,
            checkInTime: record.checkInTime,
            checkOutTime: record.checkOutTime || null,
            arrived: true, // Если запись есть, значит пришел
            departed: Boolean(record.checkOutTime),
            status: record.status || (record.checkOutTime ? 'completed' : 'present')
          };
        });

        console.log('Processed reports:', reports);
        return reports;
        
      } catch (error: unknown) {
        // Если получили 403 при попытке использовать персональный endpoint, пробуем обычный
        if (getErrorStatus(error) === 403 && usePersonalEndpoint) {
          console.warn('Access denied to personal endpoint, falling back to regular endpoint');
          const fallbackEndpoint = '/reports/attendance';
          console.log(`Retrying with fallback endpoint: ${fallbackEndpoint}`);
          
          const fallbackResponse = await api.get<Report[]>(`${fallbackEndpoint}?${queryParams}`, {
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });
          
          if (!fallbackResponse.data) {
            console.error('No data received from attendance reports API (fallback)');
            throw new Error('No data received from API');
          }

          if (!Array.isArray(fallbackResponse.data)) {
            console.error('Invalid attendance reports data format (fallback):', fallbackResponse.data);
            throw new Error('Invalid data format received from API');
          }

          // Логируем полученные данные из fallback
          console.log('Raw fallback response data:', fallbackResponse.data);

          // Проверяем каждую запись и логируем для отладки
          const fallbackReports = fallbackResponse.data.map(record => {
            console.log('Processing fallback report record:', record);
            return {
              ...record,
              // Убеждаемся, что все поля присутствуют
              date: record.date || record.checkInTime,
              checkInTime: record.checkInTime,
              checkOutTime: record.checkOutTime || null,
              arrived: true, // Если запись есть, значит пришел
              departed: Boolean(record.checkOutTime),
              status: record.status || (record.checkOutTime ? 'completed' : 'present')
            };
          });

          console.log('Processed fallback reports:', fallbackReports);
          return fallbackReports;
        }
        throw error;
      }

    } catch (error: unknown) {
      console.error('Error fetching attendance reports:', error);
      throw error;
    }
  },

  // CRUD операции для записей отчетов (без создания)

  // Универсальные CRUD операции для записей отчетов
  updateRecord: async (id: number, recordType: 'attendance' | 'time_log', data: Partial<{
    userId: number;
    projectId: number;
    date: string;
    checkInTime?: string;
    checkOutTime?: string;
    notes?: string;
    geoPosition?: { latitude: number; longitude: number };
    type: 'work' | 'status' | 'time_log';
    status: string;
    workHours?: number;
    statusReason?: string;
    isOvertime?: boolean;
    overtimeReason?: string;
  }>): Promise<Report> => {
    try {
      console.log(`Updating ${recordType} record ${id}:`, data);
      const response = await api.put<Report>(`/reports/record/${id}?type=${recordType}`, data);
      console.log(`${recordType} record updated successfully:`, response.data);
      return response.data;
    } catch (error: unknown) {
      console.error(`Error updating ${recordType} record:`, error);
      throw new Error(getErrorMessage(error, `Ошибка при обновлении записи ${recordType === 'time_log' ? 'списания времени' : 'посещаемости'}`));
    }
  },

  deleteRecord: async (id: number, recordType: 'attendance' | 'time_log'): Promise<void> => {
    try {
      console.log(`Deleting ${recordType} record ${id}`);
      await api.delete(`/reports/record/${id}?type=${recordType}`);
      console.log(`${recordType} record deleted successfully`);
    } catch (error: unknown) {
      console.error(`Error deleting ${recordType} record:`, error);
      throw new Error(getErrorMessage(error, `Ошибка при удалении записи ${recordType === 'time_log' ? 'списания времени' : 'посещаемости'}`));
    }
  },

  // Старые методы для обратной совместимости
  updateAttendanceRecord: async (id: number, data: Partial<{
    userId: number;
    projectId: number;
    date: string;
    checkInTime?: string;
    checkOutTime?: string;
    notes?: string;
    geoPosition?: { latitude: number; longitude: number };
    type: 'work' | 'status';
    status: string;
    workHours?: number;
    statusReason?: string;
  }>): Promise<Report> => {
    try {
      console.log(`Updating attendance record ${id}:`, data);
      const response = await api.put<Report>(`/reports/attendance/${id}`, data);
      console.log('Attendance record updated successfully:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('Error updating attendance record:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при обновлении записи посещаемости'));
    }
  },

  deleteAttendanceRecord: async (id: number): Promise<void> => {
    try {
      console.log(`Deleting attendance record ${id}`);
      await api.delete(`/reports/attendance/${id}`);
      console.log('Attendance record deleted successfully');
    } catch (error: unknown) {
      console.error('Error deleting attendance record:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при удалении записи посещаемости'));
    }
  },

  deleteAttendanceRecords: async (ids: number[]): Promise<void> => {
    try {
      console.log('Deleting multiple attendance records:', ids);
      await api.post('/reports/attendance/delete-batch', { ids });
      console.log('Multiple attendance records deleted successfully');
    } catch (error: unknown) {
      console.error('Error deleting multiple attendance records:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при удалении записей посещаемости'));
    }
  },

  deleteTimeLogRecords: async (ids: number[]): Promise<void> => {
    try {
      console.log('Deleting multiple time log records:', ids);
      await api.post('/reports/time-log/delete-batch', { ids });
      console.log('Multiple time log records deleted successfully');
    } catch (error: unknown) {
      console.error('Error deleting multiple time log records:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при удалении записей списания времени'));
    }
  },

  getProjects: async (): Promise<ProjectStats[]> => {
    try {
      const timestamp = new Date().getTime();
      
      // Determine endpoint based on user role
      const usePersonalEndpoint = shouldUsePersonalEndpoints();
      const endpoint = usePersonalEndpoint ? '/reports/personal/projects' : '/reports/projects';
      
      console.log(`Fetching project reports using endpoint: ${endpoint}, usePersonalEndpoint: ${usePersonalEndpoint}`);
      
      try {
        const response = await api.get<ProjectStats[]>(`${endpoint}?_t=${timestamp}`, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });

        if (!response.data) {
          console.error('No data received from project reports API');
          throw new Error('No data received from API');
        }

        if (!Array.isArray(response.data)) {
          console.error('Invalid project reports data format:', response.data);
          throw new Error('Invalid data format received from API');
        }

        return response.data;
      } catch (error: unknown) {
        // Если получили 403 при попытке использовать персональный endpoint, пробуем обычный
        if (getErrorStatus(error) === 403 && usePersonalEndpoint) {
          console.warn('Access denied to personal projects endpoint, falling back to regular endpoint');
          const fallbackEndpoint = '/reports/projects';
          console.log(`Retrying with fallback endpoint: ${fallbackEndpoint}`);
          
          const fallbackResponse = await api.get<ProjectStats[]>(`${fallbackEndpoint}?_t=${timestamp}`, {
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });

          if (!fallbackResponse.data) {
            console.error('No data received from project reports API (fallback)');
            throw new Error('No data received from API');
          }

          if (!Array.isArray(fallbackResponse.data)) {
            console.error('Invalid project reports data format (fallback):', fallbackResponse.data);
            throw new Error('Invalid data format received from API');
          }

          return fallbackResponse.data;
        }
        throw error;
      }
    } catch (error: unknown) {
      console.error('Error fetching project reports:', error);
      throw error;
    }
  }
}

// Timesheet API
export const timesheet = {
  getReport: async (year: number, month: number, forceRefresh = false): Promise<TimesheetData[]> => {
    try {
      const queryParams = new URLSearchParams()
      queryParams.append('year', year.toString())
      queryParams.append('month', month.toString())
      if (forceRefresh) queryParams.append('_t', Date.now().toString())
      
      // Determine endpoint based on user role
      const usePersonalEndpoint = shouldUsePersonalEndpoints();
      const endpoint = usePersonalEndpoint ? '/timesheet/personal' : '/timesheet';
      
      console.log(`Fetching timesheet report with params: ${queryParams.toString()}, using endpoint: ${endpoint}, usePersonalEndpoint: ${usePersonalEndpoint}`)
      
      try {
        const response = await api.get<TimesheetData[]>(`${endpoint}?${queryParams}`, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        })

        if (!response.data) {
          console.warn('No data received from timesheet API, returning empty array')
          return []
        }

        if (!Array.isArray(response.data)) {
          console.error('Invalid timesheet data format:', response.data)
          throw new Error('Invalid data format received from API')
        }

        console.log('Timesheet data received:', response.data)
        return response.data
      } catch (error: unknown) {
        // Если получили 403 при попытке использовать персональный endpoint, пробуем обычный
        if (getErrorStatus(error) === 403 && usePersonalEndpoint) {
          console.warn('Access denied to personal timesheet endpoint, falling back to regular endpoint');
          const fallbackEndpoint = '/timesheet';
          console.log(`Retrying with fallback endpoint: ${fallbackEndpoint}`);
          
          const fallbackResponse = await api.get<TimesheetData[]>(`${fallbackEndpoint}?${queryParams}`, {
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          })

          if (!fallbackResponse.data) {
            console.warn('No data received from timesheet API (fallback), returning empty array')
            return []
          }

          if (!Array.isArray(fallbackResponse.data)) {
            console.error('Invalid timesheet data format (fallback):', fallbackResponse.data)
            throw new Error('Invalid data format received from API')
          }

          console.log('Timesheet data received (fallback):', fallbackResponse.data)
          return fallbackResponse.data
        }
        throw error;
      }

    } catch (error: unknown) {
      console.error('Error fetching timesheet report:', error)
      throw error
    }
  }
}

// Activity Logs API
export const IActivityLogs = {
  list: async (): Promise<IActivityLog[]> => {
    try {
      console.log('Fetching activity logs from API...');
      const timestamp = new Date().getTime();
      const response = await api.get<IActivityLog[]>(`/activity-logs?_t=${timestamp}`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (!response.data) {
        console.error('No data received from activity logs API');
        throw new Error('No data received from API');
      }

      if (!Array.isArray(response.data)) {
        console.error('Invalid activity logs data format:', response.data);
        throw new Error('Invalid data format received from API');
      }

      console.log(`Fetched ${response.data.length} activity logs:`, response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('Error fetching activity logs:', error);
      throw error;
    }
  },

  create: async (data: {
    action: string
    projectId: number
    description?: string
    entityType?: string
    entityId?: number
  }): Promise<IActivityLog> => {
    const response = await api.post<IActivityLog>('/activity-logs', data)
    return response.data
  }
}

// Users API
export const users = {
  list: async (): Promise<IUser[]> => {
    try {
      console.log('Fetching users from API...');
      const timestamp = new Date().getTime();
      const response = await api.get<IUser[]>(`/users?_t=${timestamp}`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (!response.data) {
        console.error('No data received from users API');
        throw new Error('No data received from API');
      }

      if (!Array.isArray(response.data)) {
        console.error('Invalid users data format:', response.data);
        throw new Error('Invalid data format received from API');
      }

      return response.data;
    } catch (error: unknown) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  getManagers: async (): Promise<IUser[]> => {
    try {
      console.log('Fetching managers from API...');
      const response = await api.get<IUser[]>('/users/managers');
      
      if (!response.data) {
        console.error('No data received from managers API');
        return [];
      }

      if (!Array.isArray(response.data)) {
        console.error('Invalid managers data format:', response.data);
        return [];
      }

      return response.data;
    } catch (error: unknown) {
      console.error('Error fetching managers:', error);
      return [];
    }
  },

  getSeniorEngineers: async (): Promise<IUser[]> => {
    try {
      console.log('Fetching senior engineers from API...');
      const response = await api.get<IUser[]>('/users/senior-engineers');
      if (!response.data) {
        console.error('No data received from senior engineers API');
        throw new Error('No data received from API');
      }
      if (!Array.isArray(response.data)) {
        console.error('Invalid senior engineers data format:', response.data);
        throw new Error('Invalid data format received from API');
      }
      return response.data;
    } catch (error: unknown) {
      console.error('Error fetching senior engineers:', error);
      throw error;
    }
  },

  // Уволенные сотрудники из локальной БД (IsTerminated=true).
  // В отличие от ldap.getTerminatedUsers() (сырые данные из LDAP), этот метод возвращает
  // локальные записи User со всеми сохраненными связями (проекты, посещаемость, списания
  // времени, логи) - статус увольнения синхронизируется автоматически из LDAP.
  getTerminated: async (): Promise<IUser[]> => {
    try {
      console.log('Fetching terminated users from API...');
      const response = await api.get<IUser[]>('/users/terminated');

      if (!response.data) {
        console.error('No data received from terminated users API');
        return [];
      }

      if (!Array.isArray(response.data)) {
        console.error('Invalid terminated users data format:', response.data);
        return [];
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching terminated users:', error);
      throw error;
    }
  },

  get: async (id: number): Promise<IUser> => {
    try {
      const timestamp = new Date().getTime();
      console.log(`Fetching user data for ID ${id}...`);
      
      const response = await api.get<IUser>(`/users/${id}?include=projects&_t=${timestamp}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      console.log(`Received user data for ID ${id}:`, response.data);
      
      // Normalize user data to ensure consistent format
      const normalizedUser: IUser = {
        id: response.data.id,
        name: response.data.name,
        email: response.data.email,
        accessLevel: response.data.accessLevel,
        employmentType: response.data.employmentType || 'permanent',
        timeLoggingEnabled: response.data.timeLoggingEnabled !== undefined ? response.data.timeLoggingEnabled : true,
        chatId: response.data.chatId, // Добавляем chatId
        createdAt: response.data.createdAt,
        updatedAt: response.data.updatedAt,
        projects: response.data.projects || []
      };
      
      return normalizedUser;
    } catch (error: unknown) {
      console.error(`Error fetching user ${id}:`, error);
      throw error;
    }
  },
  
  create: async (data: CreateUserData): Promise<IUser> => {
    try {
      console.log('Creating new user with data:', data);
      const response = await api.post<AuthResponse>('/auth/register', data);
      console.log('User creation response:', response.data);
      return response.data.user;
    } catch (error: unknown) {
      console.error('Error creating user:', error);
      if (isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Failed to create user');
      }
      throw error;
    }
  },

  update: async (id: number, data: Partial<UpdateUserData>): Promise<IUser> => {
    const response = await api.put<IUser>(`/users/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    try {
      const response = await api.delete(`/users/${id}`);
      if (response.status === 204 || response.status === 200) {
        return;
      }
      throw new Error('Unexpected response status: ' + response.status);
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        throw new Error(error.response?.data?.error || 'Unknown server error');
      }
      throw error;
    }
  },

  getCurrentUser: async (): Promise<IUser> => {
    const response = await api.get<IUser>('/user')
    return response.data
  }
}

// Добавляем новую функцию для получения сотрудников проекта
export const projectUsers = {
  list: async (projectId: number): Promise<IUser[]> => {
    try {
      console.log(`Fetching users for project ${projectId}...`);
      const timestamp = new Date().getTime();
      
      // Получаем данные о проекте, включая сотрудников
      try {
        const projectResponse = await api.get<Project>(`/projects/${projectId}?include=users&_t=${timestamp}`, {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        
        console.log('Project API response:', projectResponse.data);
        
        // Если у проекта есть сотрудники, вернем их
        if (projectResponse.data.users && Array.isArray(projectResponse.data.users) && projectResponse.data.users.length > 0) {
          console.log(`Found ${projectResponse.data.users.length} users in project data`);
          return projectResponse.data.users.map(user => ({
            id: user.id,
            name: user.name || 'Без имени',
            email: user.email || '',
            accessLevel: user.accessLevel || 'engineer',
            employmentType: user.employmentType || 'permanent',
            timeLoggingEnabled: user.timeLoggingEnabled !== undefined ? user.timeLoggingEnabled : true,
            chatId: user.chatId, // Добавляем chatId
            createdAt: user.createdAt || '',
            updatedAt: user.updatedAt || ''
          }));
        } else {
          console.log(`Project ${projectId} has no assigned users`);
        }
      } catch (error: unknown) {
        console.error('Error fetching project data with users:', error);
      }
      
      // Если не удалось получить сотрудников через проект, пробуем прямой API запрос
      try {
        console.log(`Attempting direct API call to get users for project ${projectId}...`);
        // Этот запрос должен быть реализован на бэкенде для получения сотрудников проекта
        const projectUsersResponse = await api.get<IUser[]>(`/projects/${projectId}/users?_t=${timestamp}`, {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        
        if (projectUsersResponse.data && Array.isArray(projectUsersResponse.data)) {
          console.log(`Found ${projectUsersResponse.data.length} users via dedicated API endpoint`);
          return projectUsersResponse.data;
        }
      } catch (error: unknown) {
        console.error(`Error fetching users via dedicated endpoint for project ${projectId}:`, error);
        // Подавляем ошибку, если эндпоинт не существует
      }
      
      // Если все методы не сработали, вернем пустой массив
      console.warn(`Could not find users for project ${projectId} using any available method`);
      return [];
    } catch (error: unknown) {
      console.error(`Error in projectUsers.list for project ${projectId}:`, error);
      return [];
    }
  }
}

// News API
export const news = {
  list: async (params?: NewsListParams): Promise<PaginatedNewsResponse> => {
    try {
      console.log('🔄 [API] Запрос списка новостей с параметрами:', params)
      
      // Строим параметры запроса
      const queryParams = new URLSearchParams()
      if (params?.page) queryParams.append('page', params.page.toString())
      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.year) queryParams.append('year', params.year.toString())
      if (params?.month) queryParams.append('month', params.month.toString())
      
      const url = `/news${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
      console.log('🔗 [API] URL запроса:', url)
      
      const response = await api.get<PaginatedNewsResponse>(url)
      console.log('✅ [API] Пагинированный ответ от сервера:', {
        total: response.data.total,
        page: response.data.page,
        limit: response.data.limit,
        totalPages: response.data.totalPages,
        hasMore: response.data.hasMore,
        newsCount: response.data.news.length
      })
      
      // Подробные логи для каждой новости
      response.data.news.forEach((newsItem, index) => {
        console.log(`📰 [API] Новость ${index + 1} на странице ${response.data.page}:`, {
          id: newsItem.id,
          title: newsItem.title,
          coverImage: newsItem.coverImage,
          videoFile: newsItem.videoFile,
          videoFileType: typeof newsItem.videoFile,
          videoFileExists: newsItem.videoFile ? true : false,
          videoFileLength: newsItem.videoFile ? newsItem.videoFile.length : null,
          coverImageType: typeof newsItem.coverImage,
          hasProtocol: newsItem.coverImage ? (newsItem.coverImage.startsWith('http') || newsItem.coverImage.startsWith('https')) : false,
          videoHasProtocol: newsItem.videoFile ? (newsItem.videoFile.startsWith('http') || newsItem.videoFile.startsWith('https')) : false
        })
      })
      
      return response.data
    } catch (error: unknown) {
      console.error('❌ [API] Ошибка при получении новостей:', error)
      throw new Error(getErrorMessage(error, 'Ошибка при получении новостей'))
    }
  },

  // Добавляем метод для обратной совместимости (получение всех новостей)
  listAll: async (): Promise<News[]> => {
    try {
      console.log('🔄 [API] Запрос всех новостей (обратная совместимость)...')
      const response = await api.get<PaginatedNewsResponse>('/news?limit=1000')
      console.log('✅ [API] Получены все новости:', response.data.news.length)
      return response.data.news
    } catch (error: unknown) {
      console.error('❌ [API] Ошибка при получении всех новостей:', error)
      throw new Error(getErrorMessage(error, 'Ошибка при получении новостей'))
    }
  },

  // Получение доступных лет новостей
  getAvailableYears: async (): Promise<number[]> => {
    try {
      console.log('🔄 [API] Запрос доступных лет новостей...')
      const response = await api.get<number[]>('/news/years')
      console.log('✅ [API] Доступные года получены:', response.data)
      
      // Проверяем что получили массив
      if (Array.isArray(response.data)) {
        return response.data
      } else {
        console.warn('⚠️ [API] Получен не массив в ответе, возвращаем текущий год')
        return [new Date().getFullYear()]
      }
    } catch (error: unknown) {
      console.error('❌ [API] Ошибка при получении доступных лет:', error)
      throw new Error(getErrorMessage(error, 'Ошибка при получении доступных лет'))
    }
  },

  get: async (id: number): Promise<News> => {
    try {
      console.log(`🔄 [API] Запрос новости с ID: ${id}`)
      const response = await api.get<News>(`/news/${id}`)
      console.log('✅ [API] Ответ от сервера:', response.data)
      
      // Подробные логи для новости
      console.log(`📰 [API] Детали новости ${id}:`, {
        id: response.data.id,
        title: response.data.title,
        coverImage: response.data.coverImage,
        videoFile: response.data.videoFile,
        coverImageType: typeof response.data.coverImage,
        coverImageLength: response.data.coverImage ? response.data.coverImage.length : 0,
        hasProtocol: response.data.coverImage ? (response.data.coverImage.startsWith('http') || response.data.coverImage.startsWith('https')) : false,
        fullCoverImageValue: JSON.stringify(response.data.coverImage)
      })
      
      return response.data
    } catch (error: unknown) {
      console.error(`❌ [API] Ошибка при получении новости ${id}:`, error)
      throw new Error(getErrorMessage(error, 'Ошибка при получении новости'))
    }
  },

  create: async (data: CreateNewsRequest): Promise<News> => {
    try {
      console.log('🔄 [API] Создание новости:', data)
      const response = await api.post<News>('/news', data)
      console.log('✅ [API] Новость создана:', response.data)
      return response.data
    } catch (error: unknown) {
      console.error('❌ [API] Ошибка при создании новости:', error)
      throw new Error(getErrorMessage(error, 'Ошибка при создании новости'))
    }
  },

  update: async (id: number, data: UpdateNewsRequest): Promise<News> => {
    try {
      console.log(`🔄 [API] Обновление новости ${id}:`, data)
      const response = await api.put<News>(`/news/${id}`, data)
      console.log('✅ [API] Новость обновлена:', response.data)
      return response.data
    } catch (error: unknown) {
      console.error(`❌ [API] Ошибка при обновлении новости ${id}:`, error)
      throw new Error(getErrorMessage(error, 'Ошибка при обновлении новости'))
    }
  },

  updateFiles: async (id: number, files: { coverImage?: string; videoFile?: string }): Promise<News> => {
    try {
      console.log(`🔄 [API] Обновление файлов новости ${id}:`, files)
      console.log('📁 [API] Детали файлов:', {
        coverImage: files.coverImage,
        videoFile: files.videoFile,
        coverImageHasProtocol: files.coverImage ? (files.coverImage.startsWith('http') || files.coverImage.startsWith('https')) : false,
        videoFileHasProtocol: files.videoFile ? (files.videoFile.startsWith('http') || files.videoFile.startsWith('https')) : false
      })
      
      const response = await api.put<News>(`/news/${id}/files`, files)
      console.log('✅ [API] Файлы новости обновлены:', response.data)
      
      // Проверяем что вернулось после обновления
      console.log('🔍 [API] Проверка обновленных файлов:', {
        returnedCoverImage: response.data.coverImage,
        returnedVideoFile: response.data.videoFile,
        coverImageHasProtocol: response.data.coverImage ? (response.data.coverImage.startsWith('http') || response.data.coverImage.startsWith('https')) : false
      })
      
      return response.data
    } catch (error: unknown) {
      console.error(`❌ [API] Ошибка при обновлении файлов новости ${id}:`, error)
      throw new Error(getErrorMessage(error, 'Ошибка при обновлении файлов новости'))
    }
  },

  delete: async (id: number): Promise<void> => {
    try {
      console.log(`🔄 [API] Удаление новости ${id}`)
      await api.delete(`/news/${id}`)
      console.log('✅ [API] Новость удалена')
    } catch (error: unknown) {
      console.error(`❌ [API] Ошибка при удалении новости ${id}:`, error)
      throw new Error(getErrorMessage(error, 'Ошибка при удалении новости'))
    }
  }
}

// Upload API
export const upload = {
  image: async (file: File): Promise<UploadResponse> => {
    try {
      console.log('🔄 [UPLOAD] Загрузка изображения:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      })
      
      const formData = new FormData()
      formData.append('file', file)
      
      const response = await api.post<UploadResponse>('/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      
      console.log('✅ [UPLOAD] Изображение загружено:', response.data)
      console.log('🔍 [UPLOAD] Детали ответа:', {
        url: response.data.url,
        urlType: typeof response.data.url,
        hasProtocol: response.data.url ? (response.data.url.startsWith('http') || response.data.url.startsWith('https')) : false,
        filename: response.data.filename,
        size: response.data.size,
        type: response.data.type,
        fullResponse: JSON.stringify(response.data)
      })
      
      return response.data
    } catch (error: unknown) {
      console.error('❌ [UPLOAD] Ошибка при загрузке изображения:', error)
      throw new Error(getErrorMessage(error, 'Ошибка при загрузке изображения'))
    }
  },

  video: async (file: File): Promise<UploadResponse> => {
    try {
      console.log('🔄 [UPLOAD] Загрузка видео:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      })
      
      const formData = new FormData()
      formData.append('file', file)
      
      const response = await api.post<UploadResponse>('/upload/video', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      
      console.log('✅ [UPLOAD] Видео загружено:', response.data)
      console.log('🔍 [UPLOAD] Детали ответа:', {
        url: response.data.url,
        urlType: typeof response.data.url,
        hasProtocol: response.data.url ? (response.data.url.startsWith('http') || response.data.url.startsWith('https')) : false,
        filename: response.data.filename,
        size: response.data.size,
        type: response.data.type,
        fullResponse: JSON.stringify(response.data)
      })
      
      return response.data
    } catch (error: unknown) {
      console.error('❌ [UPLOAD] Ошибка при загрузке видео:', error)
      throw new Error(getErrorMessage(error, 'Ошибка при загрузке видео'))
    }
  }
}

// LDAP API
export const ldap = {
  // Проверка подключения к LDAP
  testConnection: async (): Promise<LDAPConnectionTest> => {
    try {
      console.log('🔄 [LDAP] Проверка подключения к LDAP...');
      const response = await api.get<LDAPConnectionTest>('/ldap/test');
      console.log('✅ [LDAP] Подключение проверено:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('❌ [LDAP] Ошибка при проверке подключения:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при проверке подключения к LDAP'));
    }
  },

  // Получение списка пользователей из LDAP
  getUsers: async (): Promise<LDAPUser[]> => {
    try {
      console.log('🔄 [LDAP] Получение списка пользователей из LDAP...');
      const response = await api.get<LDAPUser[]>('/ldap/users');
      console.log('✅ [LDAP] Получен список пользователей:', response.data.length);
      return response.data;
    } catch (error: unknown) {
      console.error('❌ [LDAP] Ошибка при получении пользователей:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при получении пользователей из LDAP'));
    }
  },

  // Получение данных пользователя из LDAP
  getUser: async (username: string): Promise<LDAPUser> => {
    try {
      console.log(`🔄 [LDAP] Получение данных пользователя ${username} из LDAP...`);
      const response = await api.get<LDAPUser>(`/ldap/users/${username}`);
      console.log('✅ [LDAP] Получены данные пользователя:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error(`❌ [LDAP] Ошибка при получении пользователя ${username}:`, error);
      throw new Error(getErrorMessage(error, 'Ошибка при получении пользователя из LDAP'));
    }
  },

  // Синхронизация одного пользователя
  syncUser: async (username: string): Promise<IUser> => {
    try {
      console.log(`🔄 [LDAP] Синхронизация пользователя ${username}...`);
      const response = await api.post<IUser>(`/ldap/sync/${username}`);
      console.log('✅ [LDAP] Пользователь синхронизирован:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error(`❌ [LDAP] Ошибка при синхронизации пользователя ${username}:`, error);
      throw new Error(getErrorMessage(error, 'Ошибка при синхронизации пользователя'));
    }
  },

  // Получение списка уволенных пользователей из LDAP
  getTerminatedUsers: async (): Promise<LDAPUser[]> => {
    try {
      console.log('🔄 [LDAP] Получение списка уволенных пользователей из LDAP...');
      const response = await api.get<LDAPUser[]>('/ldap/terminated-users');
      console.log('✅ [LDAP] Получен список уволенных пользователей:', response.data.length);
      return response.data;
    } catch (error: unknown) {
      console.error('❌ [LDAP] Ошибка при получении уволенных пользователей:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при получении уволенных пользователей из LDAP'));
    }
  },

  // Получение списка подрядчиков из LDAP
  getContractors: async (): Promise<LDAPUser[]> => {
    try {
      console.log('🔄 [LDAP] Получение списка подрядчиков из LDAP...');
      const response = await api.get<LDAPUser[]>('/ldap/contractors');
      console.log('✅ [LDAP] Получен список подрядчиков:', response.data.length);
      return response.data;
    } catch (error: unknown) {
      console.error('❌ [LDAP] Ошибка при получении подрядчиков:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при получении подрядчиков из LDAP'));
    }
  },

  // Массовая синхронизация всех пользователей
  syncAllUsers: async (): Promise<LDAPSyncResult> => {
    try {
      console.log('🔄 [LDAP] Массовая синхронизация пользователей...');
      const response = await api.post<LDAPSyncResult>('/ldap/sync-all');
      console.log('✅ [LDAP] Массовая синхронизация завершена:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('❌ [LDAP] Ошибка при массовой синхронизации:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при массовой синхронизации пользователей'));
    }
  }
};

// API для работы со списанием времени
export const timeLogs = {
  list: async (userId?: number, startDate?: string, endDate?: string): Promise<TimeLogEntry[]> => {
    try {
      console.log('Fetching time logs from API...');
      const queryParams = new URLSearchParams();
      // != null (не !userId) — иначе userId === 0 будет ошибочно пропущен как falsy,
      // и запрос вернёт time_logs ВСЕХ пользователей вместо одного (см. историю бага).
      if (userId != null) queryParams.append('userId', userId.toString());
      if (startDate) queryParams.append('startDate', startDate);
      if (endDate) queryParams.append('endDate', endDate);
      
      const url = `/time-logs${queryParams.toString() ? `?${queryParams}` : ''}`;
      const response = await api.get<TimeLogEntry[]>(url, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      console.log('Time logs fetched successfully:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('Error fetching time logs:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при получении записей времени'));
    }
  },

  create: async (data: CreateTimeLogRequest): Promise<TimeLogEntry> => {
    try {
      console.log('Creating time log with data:', data);
      const response = await api.post<TimeLogEntry>('/time-logs', data);
      console.log('Time log created successfully:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('Error creating time log:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при создании записи времени'));
    }
  },

  createBatch: async (data: CreateTimeLogRequest[]): Promise<TimeLogEntry[]> => {
    try {
      console.log('Creating batch time logs with data:', data);
      const response = await api.post<TimeLogEntry[]>('/time-logs/batch', { timeLogs: data });
      console.log('Batch time logs created successfully:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('Error creating batch time logs:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при создании записей времени'));
    }
  },

  createBatchForUser: async (data: CreateTimeLogRequest[], userId: number): Promise<TimeLogEntry[]> => {
    try {
      console.log('Creating batch time logs for user:', userId, 'with data:', data);
      const response = await api.post<TimeLogEntry[]>('/time-logs/batch', 
        { timeLogs: data }, 
        { 
          headers: { 
            'X-Target-User-ID': userId.toString() 
          } 
        }
      );
      console.log('Batch time logs created successfully for user:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('Error creating batch time logs for user:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при создании записей времени для сотрудника'));
    }
  },

  update: async (id: number, data: UpdateTimeLogRequest): Promise<TimeLogEntry> => {
    try {
      console.log(`Updating time log ${id} with data:`, data);
      const response = await api.put<TimeLogEntry>(`/time-logs/${id}`, data);
      console.log('Time log updated successfully:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('Error updating time log:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при обновлении записи времени'));
    }
  },

  delete: async (id: number): Promise<void> => {
    try {
      console.log(`Deleting time log ${id}`);
      await api.delete(`/time-logs/${id}`);
      console.log('Time log deleted successfully');
    } catch (error: unknown) {
      console.error('Error deleting time log:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при удалении записи времени'));
    }
  },

  getTodayTotal: async (userId?: number): Promise<{ totalHours: number; isOvertime: boolean }> => {
    try {
      console.log('Getting today total hours for user:', userId);
      const queryParams = userId ? `?userId=${userId}` : '';
      const response = await api.get<{ totalHours: number; isOvertime: boolean }>(`/time-logs/today-total${queryParams}`);
      console.log('Today total hours fetched:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('Error getting today total hours:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при получении общего времени за сегодня'));
    }
  },

  /** Получить overtime-записи сотрудников отдела (для РО) */
  getDepartmentOvertime: async (startDate?: string, endDate?: string): Promise<TimeLogEntry[]> => {
    try {
      const queryParams = new URLSearchParams();
      if (startDate) queryParams.append('startDate', startDate);
      if (endDate) queryParams.append('endDate', endDate);
      queryParams.append('_t', Date.now().toString());
      const url = `/time-logs/department-overtime${queryParams.toString() ? `?${queryParams}` : ''}`;
      const response = await api.get<TimeLogEntry[]>(url);
      return response.data;
    } catch (error: unknown) {
      console.error('Error fetching department overtime logs:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при получении переработок отдела'));
    }
  },

  /** Оставить ответ руководителя отдела на overtime-комментарий */
  replyToOvertime: async (id: number, reply: string): Promise<TimeLogEntry> => {
    try {
      const response = await api.post<TimeLogEntry>(`/time-logs/${id}/reply`, { reply });
      return response.data;
    } catch (error: unknown) {
      console.error('Error replying to overtime comment:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при отправке ответа'));
    }
  }
};

// Telegram API
export const telegram = {
  // Отправить напоминание ответственному о подтверждении ухода
  sendApprovalReminder: async (attendanceId: number): Promise<{
    message: string;
    responsible: string;
  }> => {
    try {
      console.log(`Sending approval reminder for attendance ${attendanceId}`);
      const response = await api.post('/telegram/send-reminder', { attendanceId });
      console.log('Approval reminder sent:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('Error sending approval reminder:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при отправке напоминания'));
    }
  },

  // Запросить дополнительную информацию у сотрудника
  requestMoreInfo: async (data: {
    attendanceId: number;
    question: string;
  }): Promise<{
    message: string;
    employee: string;
  }> => {
    try {
      console.log('Requesting more info for attendance:', data);
      const response = await api.post('/telegram/request-info', data);
      console.log('Info request sent:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('Error requesting more info:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при запросе информации'));
    }
  }
};

// Production Calendar API
export const calendar = {
  /** Производственный календарь за месяц: карты выходных и предпраздничных дней */
  getMonth: async (year: number, month: number): Promise<{ weekendDays: Record<number, boolean>; preHolidayDays: Record<number, boolean> }> => {
    try {
      const response = await api.get<{ weekendDays: Record<number, boolean>; preHolidayDays: Record<number, boolean> }>(
        `/calendar/${year}/${month}`
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching production calendar:', error)
      throw new Error(getErrorMessage(error, 'Ошибка при получении производственного календаря'))
    }
  },
}

// Timesheet HR API
export const timesheetHR = {
  // Получить записи табеля (возвращает { records, weekendDays, preHolidayDays })
  getRecords: async (year?: number, month?: number, userId?: number): Promise<{ records: IAttendance[]; weekendDays: Record<number, boolean>; preHolidayDays: Record<number, boolean> }> => {
    try {
      const queryParams = new URLSearchParams();
      if (year) queryParams.append('year', year.toString());
      if (month) queryParams.append('month', month.toString());
      if (userId) queryParams.append('userId', userId.toString());
      
      const url = `/timesheet/records${queryParams.toString() ? `?${queryParams}` : ''}`;
      const response = await api.get<{ records: IAttendance[]; weekendDays: Record<number, boolean>; preHolidayDays: Record<number, boolean> }>(url);
      // Обратная совместимость: если вернулся массив (старый формат), адаптируем
      if (Array.isArray(response.data)) {
        return { records: response.data as unknown as IAttendance[], weekendDays: {}, preHolidayDays: {} }
      }
      return response.data;
    } catch (error: unknown) {
      console.error('Error fetching timesheet records:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при получении записей табеля'));
    }
  },

  /**
   * Атомарно заменить записи табеля (удаление старых + создание новых в одной транзакции на сервере).
   */
  replaceRecords: async (
    replaceRecordIds: number[],
    data: {
      userId: number;
      projectId?: number;
      status?: string;
      statuses?: string[];
      startDate: string;
      endDate: string;
      workHours?: number;
      statusReason?: string;
      notes?: string;
    }
  ): Promise<{ message: string; records: IAttendance[] }> => {
    try {
      const response = await api.post<{ message: string; records: IAttendance[] }>(
        "/timesheet/records/replace",
        { ...data, replaceRecordIds }
      );
      return response.data;
    } catch (error: unknown) {
      console.error("Error replacing timesheet records:", error);
      throw new Error(getErrorMessage(error, "Ошибка при замене записей табеля"));
    }
  },

  // Создать запись табеля (statuses — несколько причин на каждый день; иначе status — одна причина)
  createRecord: async (data: {
    userId: number;
    projectId?: number;
    status?: string;
    statuses?: string[];
    startDate: string;
    endDate: string;
    workHours?: number;
    statusReason?: string;
    notes?: string;
  }): Promise<{ message: string; records: IAttendance[] }> => {
    try {
      const response = await api.post<{ message: string; records: IAttendance[] }>('/timesheet/records', data);
      return response.data;
    } catch (error: unknown) {
      console.error('Error creating timesheet record:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при создании записи табеля'));
    }
  },

  // Обновить запись табеля
  updateRecord: async (id: number, data: {
    projectId?: number;
    status?: string;
    date?: string;
    workHours?: number;
    statusReason?: string;
    notes?: string;
  }): Promise<IAttendance> => {
    try {
      const response = await api.put<IAttendance>(`/timesheet/records/${id}`, data);
      return response.data;
    } catch (error: unknown) {
      console.error('Error updating timesheet record:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при обновлении записи табеля'));
    }
  },

  // Удалить запись табеля
  deleteRecord: async (id: number): Promise<{ message: string }> => {
    try {
      const response = await api.delete<{ message: string }>(`/timesheet/records/${id}`);
      return response.data;
    } catch (error: unknown) {
      console.error('Error deleting timesheet record:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при удалении записи табеля'));
    }
  }
};

// Approval API
export const approval = {
  // Получить данные для подтверждения
  getData: async (projectId?: number, startDate?: string, endDate?: string): Promise<ApprovalEmployeeData[]> => {
    try {
      console.log('Fetching approval data from API...');
      const queryParams = new URLSearchParams();
      if (projectId) queryParams.append('projectId', projectId.toString());
      if (startDate) queryParams.append('startDate', startDate);
      if (endDate) queryParams.append('endDate', endDate);
      
      const url = `/approval/data${queryParams.toString() ? `?${queryParams}` : ''}`;
      const response = await api.get<ApprovalEmployeeData[]>(url, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      console.log('Approval data fetched successfully:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('Error fetching approval data:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при получении данных подтверждения'));
    }
  },

  // Подтвердить записи времени
  approve: async (data: ApproveTimeLogsRequest): Promise<{ message: string }> => {
    try {
      console.log('Approving time logs:', data);
      const response = await api.post<{ message: string }>('/approval/approve', data);
      console.log('Time logs approved successfully:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('Error approving time logs:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при подтверждении записей'));
    }
  },

  // Отклонить записи времени
  reject: async (data: RejectTimeLogsRequest): Promise<{ message: string }> => {
    try {
      console.log('Rejecting time logs:', data);
      const response = await api.post<{ message: string }>('/approval/reject', data);
      console.log('Time logs rejected successfully:', response.data);
      return response.data;
    } catch (error: unknown) {
      console.error('Error rejecting time logs:', error);
      throw new Error(getErrorMessage(error, 'Ошибка при отклонении записей'));
    }
  }
};

// Biometria API
/**
 * Stand-методы идут через серверный прокси Next.js (/api/biometria/* → route handler),
 * который подставляет X-Stand-Key из серверного env — ключ не попадает в браузерный бандл.
 * Админ-методы идут через axios-инстанс напрямую на бэкенд с JWT.
 */
async function biometriaFetch<T = unknown>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(`/api/biometria/${path}`, init)
  const data = await resp.json().catch(() => null)
  if (!resp.ok) {
    const message =
      data && typeof data === 'object' && 'error' in data && typeof data.error === 'string'
        ? data.error
        : `Ошибка запроса (${resp.status})`
    throw new Error(message)
  }
  return data as T
}

export const biometriaApi = {
  getUsers: async () => biometriaFetch('users'),
  getProjects: async (userId: string) =>
    biometriaFetch<Array<{ id: number; name: string; currentStatus?: 'in' | 'out'; checkInTime?: string | null }>>(
      `projects?userId=${encodeURIComponent(userId)}`
    ),
  getStatus: async (userId: string, projectId: string) =>
    biometriaFetch(`status?userId=${encodeURIComponent(userId)}&projectId=${encodeURIComponent(projectId)}`),
  verify: async (userId: string, photo: Blob) => {
    const form = new FormData();
    form.append('userId', userId);
    form.append('photo', photo, 'photo.jpg');
    return biometriaFetch('verify', { method: 'POST', body: form });
  },
  check: async (userId: string, projectId: string, photo: Blob) => {
    const form = new FormData();
    form.append('userId', userId);
    form.append('projectId', projectId);
    form.append('photo', photo, 'photo.jpg');
    // Локальное время стенда в ISO-8601 с офсетом (напр. "2026-09-23T09:00:00+03:00"),
    // т.к. toISOString() отдаёт UTC без офсета, а бэкенду нужен офсет для «(местное)»
    form.append('clientTimestamp', formatLocalIsoWithOffset());
    return biometriaFetch<{ action?: string }>('check', { method: 'POST', body: form });
  },
  // ─── Админ-эндпоинты (JWT) ─────────────────────────────────────────────────
  getProfiles: async (): Promise<Array<{ userId: number; userName: string; enrolled: boolean; enabled: boolean }>> =>
    (await api.get('/biometria/profiles')).data,
  uploadProfilePhoto: async (userId: number, file: File) => {
    const form = new FormData();
    form.append('photo', file);
    return (
      await api.post(`/biometria/profiles/${userId}/photo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    ).data;
  },
  deleteProfile: async (userId: number) =>
    (await api.delete(`/biometria/profiles/${userId}`)).data,
}

/** Локальный ISO-8601 timestamp с офсетом текущей таймзоны браузера */
function formatLocalIsoWithOffset(): string {
  const now = new Date();
  const offsetMin = -now.getTimezoneOffset(); // минуты на восток от UTC (+180 для МСК)
  const sign = offsetMin >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMin);
  const off = `${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
  // Локальное «настенное» время = момент + офсет. Смещаем вперёд, чтобы
  // toISOString() (который рендерит UTC-компоненты) выдал локальные часы,
  // затем приклеиваем реальный офсет: "13:03" + "+03:00" = 10:03 UTC.
  return new Date(now.getTime() + offsetMin * 60000).toISOString().slice(0, 19) + off;
}

export default api 